<!-- 
  @file FinanceDetails.vue
  @description
  Handles the vehicle type selection step of the finance application process.
  Features:
  - Vehicle type selection (Car/Van/Bike)
  - Form validation
  - Navigation to next step
  - Accessibility support
  - Standardized error handling via useStoreOperation
  - Loading state management
  - User-friendly error messages
  @component
-->
<template>
  <PageLayout current-page="finance-details" :header="header" :showBenefits="false">
    
    <ButtonGroup
      class="mt-4"
      :name="'vehicle-type'"
      :items="vehicleOptions"       
      :model-value="values.vehicleType"
      @update:model-value="handleSelection"
      :disabled="isNavigating || isLoading"
      ref="buttonGroupRef"
    />

  </PageLayout>
</template>
  
<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { object, number } from 'zod';
import { useRootStore } from '@/stores/rootStore';
import { createElementId, Page, ElementType } from '@/utils/elementIds';
import type { StoreState } from '@/types/storeTypes';
import { useStoreOperation } from '@/composables/useStoreOperation';
import { useConfigStore } from '@/stores/modules/configStore';
import ButtonGroup from '@/components/ButtonGroup.vue';
import { usePageNavigation } from '@/composables/usePageNavigation';
import PageLayout from '@/layouts/PageLayout.vue';
import FinanceDisclaimer from '@/components/FinanceDisclaimer.vue';
const store = useRootStore();
const configStore = useConfigStore();
const buttonGroupRef = ref<InstanceType<typeof ButtonGroup> | null>(null);
const isDefaultTheme = computed(() => !configStore.isDefaultTheme);
const { FINANCE_DETAILS_CONTENT } = configStore.affiliateConfig.contents;
const header = computed(() => FINANCE_DETAILS_CONTENT.HEADERS.TITLE);

const { isNavigating, handleNavigation } = usePageNavigation('finance-details', FINANCE_DETAILS_CONTENT.ERROR_MESSAGE);
const { isLoading } = useStoreOperation();

const vehicleOptions = FINANCE_DETAILS_CONTENT.OPTIONS.map(option => ({
    ...option,
    id: createElementId(Page.FINANCE_DETAILS, ElementType[option.label.toUpperCase() as keyof typeof ElementType])
}));

const validationSchema = toTypedSchema(
    object({
        vehicleType: number({
            required_error: FINANCE_DETAILS_CONTENT.VALIDATION.OPTION_REQUIRED
        })
    })
);

const { values, setFieldValue } = useForm<FinanceDetailsFormValues>({
    validationSchema,
    initialValues: {
        vehicleType: store.form.vehicleTypeId ?? 0
    }
});

type FinanceDetailsFormValues = {
    vehicleType: NonNullable<StoreState['form']['vehicleTypeId']>;
};

const handleSelection = async (value: number) => {
    if (isLoading.value || isNavigating.value) return;
    
    setFieldValue('vehicleType', value);
    store.form.updateFormValue([value, 'vehicleTypeId']);
    
    try {
        await handleNavigation(buttonGroupRef);
    } catch (error) {
        console.error('Selection error:', error);
    }
};

onMounted(() => {
    store.progress.setPageName('finance-details');
});
</script>
  